import React, { useState } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Card,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";
import { useMutation, useQuery } from "react-query";
import { fetchActionsUtil, sendFormData } from "../../utils/helpers";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as Yup from "yup";
import { DeleteIcon } from "../Icons";
import Datetime from "react-datetime";
import Select from "react-select";
import { FALSE } from "sass";

const validationSchema = Yup.object().shape({
  enrollment_id: Yup.number().required("Enrollment is required"),
  participants: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().nullable(),
      participant_id: Yup.number().required("Participant is required"),
      certificate_number: Yup.string().required(
        "Certificate number is required",
      ),
      issued_date: Yup.date().required("Issued date is required"),
      expiry_date: Yup.date().nullable(),
      pdf_file: Yup.mixed().nullable(),
      verification_code: Yup.string(),
      course_enrollment_status: Yup.boolean(),
    }),
  ),
});

function UpdateCertificateModal({
  show,
  onHide,
  onSuccess,
  refetch,
  certificateId = null,
  enrollmentId = null,
}) {
  const { backendUrl, token } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableParticipants, setAvailableParticipants] = useState([]);

  // Fetch certificates data
  const fetchCertificates = async () => {
    try {
      let url = `${backendUrl}/api/partnership/certificates`;
      if (certificateId) {
        url += `/${certificateId}`;
      } else if (enrollmentId) {
        url += `?enrollment_id=${enrollmentId}`;
      }

      const data = await fetchActionsUtil(url, "GET", token);

      // Handle array response (multiple certificates for an enrollment)
      const certificatesArray = Array.isArray(data.data)
        ? data.data
        : [data.data];

      // Transform certificates array to match form structure

      const transformedData = {
        enrollment_id: certificatesArray[0]?.enrollment_id,

        participants: certificatesArray.map((cert) => ({
          id: cert.id,
          participant_id: parseInt(cert.participant_id) || cert.participant_id,
          certificate_number: cert.certificate_number || "",
          issued_date: cert.issued_date || "",
          expiry_date: cert.expiry_date || "",
          pdf_file: cert.pdf_url || null,
          verification_code: cert.verification_code || "",

          full_name: cert.full_name,
          label: cert.full_name,
          value: cert.participant_id,
          email: cert?.participant?.email || "",
          course_enrollment_status:
            cert?.participant?.course_enrollment_status?.toLowerCase() ===
            "certificate issued"
              ? true
              : false,
        })),
      };

      return transformedData;
    } catch (error) {
      console.error("Error fetching certificates:", error);
      throw error;
    }
  };

  const {
    data: certificateData,
    isLoading: isLoadingCertificate,
    error,
  } = useQuery(
    ["GET_CERTIFICATE", certificateId, enrollmentId],
    () => fetchCertificates(),
    {
      enabled: show && (!!certificateId || !!enrollmentId),
      keepPreviousData: true,
    },
  );

  // Fetch enrollment data with participants
  const fetchEnrollment = async () => {
    try {
      const effectiveEnrollmentId =
        enrollmentId || certificateData?.enrollment_id;
      if (!effectiveEnrollmentId) return null;

      const data = await fetchActionsUtil(
        `${backendUrl}/api/partnership/course-enrollments/${effectiveEnrollmentId}`,
        "GET",
        token,
      );

      return data.data;
    } catch (error) {
      console.error("Error fetching enrollment:", error);
      throw error;
    }
  };

  const { data: enrollmentData } = useQuery(
    ["GET_ENROLLMENT", enrollmentId, certificateData?.enrollment_id],
    () => fetchEnrollment(),
    {
      enabled: show && (!!enrollmentId || !!certificateData?.enrollment_id),
      keepPreviousData: true,
    },
  );

  // Populate availableParticipants from enrollment data
  React.useEffect(() => {
    if (enrollmentData?.participants) {
      const participantsList = enrollmentData.participants.map((p) => ({
        value: Number(p.id),
        label: `${p.full_name} (${p.email || ""})`,
      }));

      setAvailableParticipants(participantsList);
    } else if (certificateData?.participants) {
      // Fallback to certificate data if enrollment data not available
      const participantsList = certificateData.participants.map((cert) => ({
        value: Number(cert.participant_id) || cert.participant_id,
        label: `${cert.full_name} (${cert.email || ""})`,
      }));
      setAvailableParticipants(participantsList);
    }
  }, [enrollmentData, certificateData]);

  const updateCertificateMutation = useMutation(
    (payload) => {
      // payload includes certificateIds array
      const { certificateIds, formData } = payload;

      // Update all certificates - use the first one as the base
      return sendFormData(
        `${backendUrl}/api/partnership/update-certificate`,

        formData,
      );
    },
    {
      onSuccess: ({ data }) => {
        toast.success(data?.message || "Certificates updated successfully");
        if (refetch) refetch();
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(error?.message || "Failed to update certificates");
      },
    },
  );

  const initialValues = certificateData
    ? {
        enrollment_id: certificateData?.enrollment_id || "",

        participants: certificateData?.participants || [],
      }
    : {
        enrollment_id: "",
        is_completed: 0,
        participants: [],
      };

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const formData = new FormData();
        formData.append("enrollment_id", values.enrollment_id);

        // Collect certificate IDs and append participants data
        const certificateIds = [];
        values.participants.forEach((participant, index) => {
          if (participant.id) {
            certificateIds.push(participant.id);
          }

          let participantData = {
            id: participant.id || undefined,
            participant_id: participant.participant_id,
            certificate_number: participant.certificate_number,
            issued_date: participant.issued_date,
            ...(participant.expiry_date && {
              expiry_date: participant.expiry_date,
            }),
            ...(participant.verification_code && {
              verification_code: participant.verification_code,
            }),
            ...(participant.course_enrollment_status !== undefined && {
              course_enrollment_status: participant.course_enrollment_status
                ? "Certificate Issued"
                : "Completed",
            }),

            certUrlFile: participant.certUrlFile || undefined,
          };

          formData.append("enrollment_id", values.enrollment_id);

          // Add course_id from enrollment data
          if (enrollmentData?.course_id) {
            formData.append("course_id", enrollmentData.course_id);
          }

          // Append PDF file if exists and is a File object (not a string URL)
          if (participant.pdf_file) {
            if (typeof participant.pdf_file !== "string") {
              // New file uploaded
              formData.append(
                `participants${index}[pdf_file]`,
                participant.pdf_file,
              );
              formData.append(
                `participants${index}[pdf_file_name]`,
                participant.pdf_file.name,
              );
              formData.append(
                `participants${index}[pdf_file_type]`,
                participant.pdf_file.type,
              );
            } else {
              participantData = {
                ...participantData,
                pdf_file_url: participant?.pdf_file || "",
              };
            }
          }

          formData.append(
            `participants${index}`,
            JSON.stringify(participantData),
          );
        });

        updateCertificateMutation.mutate({ certificateIds, formData });
      } catch (error) {
        toast.error("Error processing form");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  console.log(formik.errors);
  const handleClose = () => {
    formik.resetForm();
    onHide();
  };

  const isLoading =
    isSubmitting ||
    updateCertificateMutation.isLoading ||
    (show && certificateData === undefined);

  if (show && certificateData === undefined && !error) {
    return (
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading certificate data...</p>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title as={"h6"}>Update Certificate</Modal.Title>
      </Modal.Header>

      {error ? (
        <Modal.Body>
          <Alert variant="danger">
            {error?.message || "Error loading certificate data"}
          </Alert>
        </Modal.Body>
      ) : (
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Modal.Body>
              <hr className="my-4" />

              <div className="mb-4">
                <h6 className="mb-3">
                  Participant Certificates ({formik.values.participants.length})
                </h6>

                {formik.values.participants.length === 0 ? (
                  <Alert variant="info">
                    No participants found for this enrollment
                  </Alert>
                ) : (
                  <FieldArray
                    name="participants"
                    render={(arrayHelpers) => (
                      <div>
                        {formik.values.participants.map(
                          (participant, index) => (
                            <Card key={index} className="mb-3 border">
                              <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <h6>Participant {index + 1}</h6>
                                  {formik.values.participants.length > 1 && (
                                    <Button
                                      variant="link"
                                      className="text-danger p-0"
                                      onClick={() => arrayHelpers.remove(index)}
                                      title="Remove participant"
                                    >
                                      <DeleteIcon size={18} />
                                    </Button>
                                  )}
                                </div>

                                <Form.Group className="mb-3">
                                  <Form.Label>Participant Name *</Form.Label>
                                  {participant.id ? (
                                    // Existing participant - show as read-only
                                    <Form.Control
                                      type="text"
                                      disabled
                                      value={participant?.full_name}
                                      className="bg-light"
                                    />
                                  ) : (
                                    // New participant - show as selectable dropdown
                                    <Select
                                      classNamePrefix="form-select"
                                      placeholder="Select participant"
                                      name={`participants[${index}].participant_id`}
                                      value={
                                        availableParticipants.find(
                                          (p) =>
                                            p.value ===
                                            Number(
                                              formik.values.participants[index]
                                                .participant_id,
                                            ),
                                        ) || null
                                      }
                                      onChange={(selected) => {
                                        console.log(
                                          "formik select val: ",
                                          selected,
                                        );
                                        formik.setFieldValue(
                                          `participants[${index}].participant_id`,
                                          selected?.value || "",
                                        );
                                        // Also set the full_name
                                        if (selected) {
                                          formik.setFieldValue(
                                            `participants[${index}].full_name`,
                                            selected.label.split(" (")[0],
                                          );
                                        }
                                      }}
                                      options={availableParticipants}
                                      isClearable
                                      isInvalid={
                                        formik.touched.participants?.[index]
                                          ?.participant_id &&
                                        !!formik.errors.participants?.[index]
                                          ?.participant_id
                                      }
                                    />
                                  )}
                                  {formik.touched.participants?.[index]
                                    ?.participant_id &&
                                    formik.errors.participants?.[index]
                                      ?.participant_id && (
                                      <Form.Text className="text-danger">
                                        {
                                          formik.errors.participants[index]
                                            .participant_id
                                        }
                                      </Form.Text>
                                    )}
                                </Form.Group>

                                <Row>
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label>
                                        Certificate Number *
                                      </Form.Label>
                                      <Form.Control
                                        type="text"
                                        name={`participants[${index}].certificate_number`}
                                        placeholder="e.g., CERT-2024-001"
                                        value={
                                          formik.values.participants[index]
                                            .certificate_number
                                        }
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={
                                          formik.touched.participants?.[index]
                                            ?.certificate_number &&
                                          !!formik.errors.participants?.[index]
                                            ?.certificate_number
                                        }
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        {
                                          formik.errors.participants?.[index]
                                            ?.certificate_number
                                        }
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label>Verification Code</Form.Label>
                                      <Form.Control
                                        type="text"
                                        name={`participants[${index}].verification_code`}
                                        placeholder="e.g., VERIFY-001"
                                        value={
                                          formik.values.participants[index]
                                            .verification_code || ""
                                        }
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                      />
                                    </Form.Group>
                                  </Col>
                                </Row>

                                <Row>
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Check
                                        type="switch"
                                        id={`participants[${index}].course_enrollment_status`}
                                        name={`participants[${index}].course_enrollment_status`}
                                        label="Issue Certificate"
                                        checked={
                                          formik.values.participants[index]
                                            .course_enrollment_status || false
                                        }
                                        onChange={(e) => {
                                          formik.setFieldValue(
                                            `participants[${index}].course_enrollment_status`,
                                            e.target.checked,
                                          );
                                        }}
                                      />
                                    </Form.Group>
                                  </Col>
                                </Row>

                                <Row>
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label>Issued Date *</Form.Label>
                                      <Datetime
                                        dateFormat="MMM DD, YYYY"
                                        timeFormat={false}
                                        closeOnSelect={true}
                                        closeOnClickOutside={true}
                                        name={`participants[${index}].issued_date`}
                                        inputProps={{
                                          className: `date-input form-control`,
                                          placeholder: "Select issued date",
                                          readOnly: true,
                                        }}
                                        value={
                                          formik.values.participants[index]
                                            .issued_date
                                        }
                                        onChange={(date) => {
                                          formik.setFieldValue(
                                            `participants[${index}].issued_date`,
                                            date
                                              ? new Date(date).toISOString()
                                              : "",
                                          );
                                        }}
                                        onBlur={() =>
                                          formik.setFieldTouched(
                                            `participants[${index}].issued_date`,
                                            true,
                                          )
                                        }
                                      />
                                      {formik.touched.participants?.[index]
                                        ?.issued_date &&
                                        formik.errors.participants?.[index]
                                          ?.issued_date && (
                                          <Form.Text className="text-danger">
                                            {
                                              formik.errors.participants[index]
                                                .issued_date
                                            }
                                          </Form.Text>
                                        )}
                                    </Form.Group>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label>Expiry Date</Form.Label>
                                      <Datetime
                                        dateFormat="MMM DD, YYYY"
                                        timeFormat={false}
                                        closeOnSelect={true}
                                        closeOnClickOutside={true}
                                        name={`participants[${index}].expiry_date`}
                                        inputProps={{
                                          className: `date-input form-control`,
                                          placeholder: "Select expiry date",
                                          readOnly: true,
                                        }}
                                        value={
                                          formik.values.participants[index]
                                            .expiry_date || ""
                                        }
                                        onChange={(date) => {
                                          formik.setFieldValue(
                                            `participants[${index}].expiry_date`,
                                            date
                                              ? new Date(date).toISOString()
                                              : "",
                                          );
                                        }}
                                        onBlur={() =>
                                          formik.setFieldTouched(
                                            `participants[${index}].expiry_date`,
                                            true,
                                          )
                                        }
                                      />
                                      {formik.touched.participants?.[index]
                                        ?.expiry_date &&
                                        formik.errors.participants?.[index]
                                          ?.expiry_date && (
                                          <Form.Text className="text-danger">
                                            {
                                              formik.errors.participants[index]
                                                .expiry_date
                                            }
                                          </Form.Text>
                                        )}
                                    </Form.Group>
                                  </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                  <Form.Label>
                                    Update Certificate PDF
                                  </Form.Label>
                                  {formik.values.participants[index].pdf_file &&
                                    typeof formik.values.participants[index]
                                      .pdf_file === "string" && (
                                      <div className="alert alert-info mb-2">
                                        Current:{" "}
                                        {
                                          formik.values.participants[index]
                                            .pdf_file
                                        }
                                      </div>
                                    )}
                                  <Form.Control
                                    type="file"
                                    accept=".pdf"
                                    name={`participants[${index}].pdf_file`}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        if (file.type !== "application/pdf") {
                                          toast.error(
                                            "Please upload a valid PDF file",
                                          );
                                          return;
                                        }

                                        formik.setFieldValue(
                                          `participants[${index}].pdf_file`,
                                          file,
                                        );
                                      }
                                    }}
                                    onBlur={formik.handleBlur}
                                  />
                                  {formik.values.participants[index].pdf_file &&
                                    typeof formik.values.participants[index]
                                      .pdf_file !== "string" && (
                                      <Form.Text className="text-success">
                                        New file:{" "}
                                        {
                                          formik.values.participants[index]
                                            .pdf_file.name
                                        }
                                      </Form.Text>
                                    )}
                                </Form.Group>
                              </Card.Body>
                            </Card>
                          ),
                        )}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            arrayHelpers.push({
                              id: null,
                              participant_id: "",
                              certificate_number: "",
                              issued_date: "",
                              expiry_date: "",
                              pdf_file: null,
                              verification_code: "",
                              full_name: "",
                            })
                          }
                          className="mt-2"
                        >
                          + Add Another Participant
                        </Button>
                      </div>
                    )}
                  />
                )}
              </div>

              {Object.keys(formik.errors).length > 0 && formik.isSubmitting && (
                <Alert variant="danger">
                  Please fix the errors above before submitting.
                </Alert>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Updating...
                  </>
                ) : (
                  "Update Certificate"
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </FormikProvider>
      )}
    </Modal>
  );
}

export default UpdateCertificateModal;
